import React from 'react';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis,
  Cell,
  ReferenceLine,
  Label,
  PieChart,
  Pie,
  LabelList
} from 'recharts';

const AdvancedAnalysisDashboard = () => {
  // ANCOVA results
  const ancovaResults = {
    totalScore: {
      model: {
        rSquared: 0.984,
        adjustedRSquared: 0.982,
        observations: 60
      },
      coefficients: [
        {term: "Intercept", estimate: -0.659, stdError: 0.871, tStat: -0.757, pValue: 0.449},
        {term: "Test1 Score", estimate: 1.022, stdError: 0.025, tStat: 41.570, pValue: 0},
        {term: "Experimental Group", estimate: 4.761, stdError: 1.231, tStat: 3.868, pValue: 0.0001},
        {term: "Test1 × Group", estimate: -0.109, stdError: 0.034, tStat: -3.221, pValue: 0.0013}
      ],
      adjustedMeans: {
        control: 35.54,
        experimental: 36.43,
        difference: 0.89
      },
      effectSize: {
        cohensD: 0.88
      }
    },
    byBand: [
      {
        band: 1,
        coefficients: [
          {term: "Experimental Group", estimate: 0.793, stdError: 0.531, tStat: 1.493, pValue: 0.135}
        ],
        adjustedMeans: {
          control: 7.98,
          experimental: 8.15,
          difference: 0.17
        },
        effectSize: {
          cohensD: 0.35
        }
      },
      {
        band: 2,
        coefficients: [
          {term: "Experimental Group", estimate: 1.342, stdError: 0.361, tStat: 3.714, pValue: 0.0002}
        ],
        adjustedMeans: {
          control: 7.18,
          experimental: 7.54,
          difference: 0.35
        },
        effectSize: {
          cohensD: 0.96
        }
      },
      {
        band: 3,
        coefficients: [
          {term: "Experimental Group", estimate: 0.151, stdError: 0.406, tStat: 0.372, pValue: 0.710}
        ],
        adjustedMeans: {
          control: 6.62,
          experimental: 6.81,
          difference: 0.19
        },
        effectSize: {
          cohensD: 0.44
        }
      },
      {
        band: 4,
        coefficients: [
          {term: "Experimental Group", estimate: 0.808, stdError: 0.335, tStat: 2.410, pValue: 0.016}
        ],
        adjustedMeans: {
          control: 7.08,
          experimental: 7.25,
          difference: 0.16
        },
        effectSize: {
          cohensD: 0.54
        }
      },
      {
        band: 5,
        coefficients: [
          {term: "Experimental Group", estimate: 0.200, stdError: 0.228, tStat: 0.877, pValue: 0.381}
        ],
        adjustedMeans: {
          control: 6.62,
          experimental: 6.68,
          difference: 0.06
        },
        effectSize: {
          cohensD: 0.24
        }
      }
    ]
  };

  // Cluster analysis results
  const clusterResults = {
    k3: [
      {
        cluster: 0,
        size: 3,
        experimental: 1,
        control: 2,
        avgInitialScore: 27.33,
        avgImprovement: -0.67,
        avgBand1Imp: 0.33,
        avgBand2Imp: 0,
        avgBand3Imp: 0,
        avgBand4Imp: 0,
        avgBand5Imp: -1,
        cerfDistribution: {A2: 0, B1: 3, B2: 0},
        avgBooks: 2,
        avgMovies: 3.33,
        avgSocialMedia: 4.33
      },
      {
        cluster: 1,
        size: 44,
        experimental: 19,
        control: 25,
        avgInitialScore: 37.98,
        avgImprovement: 0.09,
        avgBand1Imp: 0.02,
        avgBand2Imp: 0,
        avgBand3Imp: 0.02,
        avgBand4Imp: 0.05,
        avgBand5Imp: 0,
        cerfDistribution: {A2: 0, B1: 18, B2: 26},
        avgBooks: 3.30,
        avgMovies: 4.18,
        avgSocialMedia: 4.41
      },
      {
        cluster: 2,
        size: 13,
        experimental: 10,
        control: 3,
        avgInitialScore: 28.62,
        avgImprovement: 2.23,
        avgBand1Imp: 0.38,
        avgBand2Imp: 1.08,
        avgBand3Imp: 0.46,
        avgBand4Imp: 0.23,
        avgBand5Imp: 0.08,
        cerfDistribution: {A2: 0, B1: 7, B2: 6},
        avgBooks: 2.85,
        avgMovies: 4.62,
        avgSocialMedia: 4.31
      }
    ],
    k4: [
      {
        cluster: 0,
        size: 38,
        experimental: 17,
        control: 21,
        avgInitialScore: 38.11,
        avgImprovement: 0.08,
        avgBand1Imp: -0.05,
        avgBand2Imp: 0,
        avgBand3Imp: 0.05,
        avgBand4Imp: 0.08,
        avgBand5Imp: 0,
        cerfDistribution: {A2: 0, B1: 16, B2: 22},
        avgBooks: 3.32,
        avgMovies: 4.21,
        avgSocialMedia: 4.47
      },
      {
        cluster: 1,
        size: 4,
        experimental: 3,
        control: 1,
        avgInitialScore: 20.50,
        avgImprovement: 2.75,
        avgBand1Imp: 0,
        avgBand2Imp: 1,
        avgBand3Imp: 1,
        avgBand4Imp: 0.5,
        avgBand5Imp: 0.25,
        cerfDistribution: {A2: 0, B1: 3, B2: 1},
        avgBooks: 3.5,
        avgMovies: 4,
        avgSocialMedia: 4.5
      },
      {
        cluster: 2,
        size: 13,
        experimental: 7,
        control: 6,
        avgInitialScore: 36.62,
        avgImprovement: 1.77,
        avgBand1Imp: 0.77,
        avgBand2Imp: 0.69,
        avgBand3Imp: 0.31,
        avgBand4Imp: 0.08,
        avgBand5Imp: -0.08,
        cerfDistribution: {A2: 0, B1: 4, B2: 9},
        avgBooks: 2.85,
        avgMovies: 4.54,
        avgSocialMedia: 4.23
      },
      {
        cluster: 3,
        size: 5,
        experimental: 3,
        control: 2,
        avgInitialScore: 23.80,
        avgImprovement: -1.20,
        avgBand1Imp: -0.20,
        avgBand2Imp: 0.20,
        avgBand3Imp: -0.60,
        avgBand4Imp: -0.20,
        avgBand5Imp: -0.40,
        cerfDistribution: {A2: 0, B1: 5, B2: 0},
        avgBooks: 2.2,
        avgMovies: 3.8,
        avgSocialMedia: 4
      }
    ]
  };

  // Format ANCOVA coefficient data for visualization
  const ancovaCoefficientsData = ancovaResults.totalScore.coefficients
    .filter(coef => coef.term !== "Intercept")
    .map(coef => ({
      term: coef.term,
      estimate: coef.estimate,
      pValue: coef.pValue,
      significant: coef.pValue < 0.05
    }));

  // Format ANCOVA adjusted means for visualization
  const adjustedMeansData = [
    { group: "Control", adjustedMean: ancovaResults.totalScore.adjustedMeans.control },
    { group: "Experimental", adjustedMean: ancovaResults.totalScore.adjustedMeans.experimental }
  ];

  // Format band-specific results for visualization
  const bandEffectSizeData = ancovaResults.byBand.map(band => ({
    band: `Band ${band.band}`,
    effectSize: band.effectSize.cohensD,
    significant: band.coefficients[0].pValue < 0.05
  }));

  // Format cluster composition data
  const clusterCompositionData = clusterResults.k3.map(cluster => ({
    cluster: `Cluster ${cluster.cluster + 1}`,
    experimental: cluster.experimental,
    control: cluster.control
  }));

  // Format cluster improvement data
  const clusterImprovementData = clusterResults.k3.map(cluster => ({
    cluster: `Cluster ${cluster.cluster + 1}`,
    improvement: cluster.avgImprovement,
    size: cluster.size
  }));

  // Format CERF distribution data
  const cerfDistributionByCluster = clusterResults.k3.map(cluster => ({
    cluster: `Cluster ${cluster.cluster + 1}`,
    A2: cluster.cerfDistribution.A2,
    B1: cluster.cerfDistribution.B1,
    B2: cluster.cerfDistribution.B2
  }));

  // Format band improvement data by cluster
  const bandImprovementByCluster = [
    {
      bandNum: 1,
      "Cluster 1": clusterResults.k3[0].avgBand1Imp,
      "Cluster 2": clusterResults.k3[1].avgBand1Imp,
      "Cluster 3": clusterResults.k3[2].avgBand1Imp
    },
    {
      bandNum: 2,
      "Cluster 1": clusterResults.k3[0].avgBand2Imp,
      "Cluster 2": clusterResults.k3[1].avgBand2Imp,
      "Cluster 3": clusterResults.k3[2].avgBand2Imp
    },
    {
      bandNum: 3,
      "Cluster 1": clusterResults.k3[0].avgBand3Imp,
      "Cluster 2": clusterResults.k3[1].avgBand3Imp,
      "Cluster 3": clusterResults.k3[2].avgBand3Imp
    },
    {
      bandNum: 4,
      "Cluster 1": clusterResults.k3[0].avgBand4Imp,
      "Cluster 2": clusterResults.k3[1].avgBand4Imp,
      "Cluster 3": clusterResults.k3[2].avgBand4Imp
    },
    {
      bandNum: 5,
      "Cluster 1": clusterResults.k3[0].avgBand5Imp,
      "Cluster 2": clusterResults.k3[1].avgBand5Imp,
      "Cluster 3": clusterResults.k3[2].avgBand5Imp
    }
  ];

  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div style={{ marginBottom: '2rem', backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)', borderRadius: '0.5rem', padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1F2937' }}>
          Advanced Statistical Analysis
        </h1>
        
        {/* ANCOVA Analysis Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#1E40AF' }}>
            ANCOVA Analysis
          </h2>
          <p style={{ marginBottom: '1rem', color: '#374151' }}>
            Analysis of Covariance (ANCOVA) allows us to assess the effect of the crash course while controlling for initial test performance.
            This provides a more rigorous assessment of the intervention's impact by accounting for different starting points.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#2563EB' }}>
                Model Summary
              </h3>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>R² (Coefficient of Determination):</span> {ancovaResults.totalScore.model.rSquared.toFixed(3)}
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Adjusted R²:</span> {ancovaResults.totalScore.model.adjustedRSquared.toFixed(3)}
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Sample Size:</span> {ancovaResults.totalScore.model.observations} students
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Effect Size (Cohen's d):</span> {ancovaResults.totalScore.effectSize.cohensD.toFixed(2)} (Large effect)
              </p>
              <p style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                The high R² value indicates that our model explains 98.4% of the variance in Test 2 scores.
                The effect size of d=0.88 represents a large practical significance.
              </p>
            </div>
            
            <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#2563EB' }}>
                Key Findings
              </h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '1rem', fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <li>
                  <span style={{ fontWeight: 600 }}>Significant group effect:</span> The experimental group showed statistically significant improvement (p&lt;0.001)
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Adjusted mean difference:</span> 0.89 points higher in experimental group
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Interaction effect:</span> The intervention had different effects based on initial performance (p=0.001)
                </li>
                <li>
                  <span style={{ fontWeight: 600 }}>Greater benefit for lower performers:</span> Negative interaction coefficient (-0.11) indicates that students with lower initial scores benefited more
                </li>
              </ul>
            </div>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              ANCOVA Coefficients
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={ancovaCoefficientsData}
                  margin={{ top: 20, right: 30, left: 120, bottom: 5 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="term" type="category" />
                  <Tooltip formatter={(value) => [value.toFixed(3), "Coefficient"]} />
                  <Bar dataKey="estimate" name="Coefficient Estimate">
                    {ancovaCoefficientsData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.significant ? (entry.estimate > 0 ? '#4CAF50' : '#F44336') : '#9E9E9E'} 
                      />
                    ))}
                  </Bar>
                  <ReferenceLine x={0} stroke="#000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              The chart shows coefficient estimates from the ANCOVA model. Statistically significant coefficients (p&lt;0.05) are 
              shown in green (positive) or red (negative). The large positive "Experimental Group" coefficient indicates the crash 
              course had a significant positive effect. The negative interaction term shows that the effect was stronger for 
              lower-performing students.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              Adjusted Mean Scores by Group
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={adjustedMeansData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="group" />
                  <YAxis domain={[30, 40]} />
                  <Tooltip />
                  <Legend />
                  <Bar name="Adjusted Mean Score" dataKey="adjustedMean" fill="#8884d8">
                    <LabelList dataKey="adjustedMean" position="top" formatter={(value) => value.toFixed(2)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              Adjusted means represent the expected Test 2 scores for each group, controlling for initial performance differences.
              After adjusting for Test 1 scores, the experimental group still outperformed the control group by 0.89 points.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              Effect Size by Band
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bandEffectSizeData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="band" />
                  <YAxis domain={[0, 1]} />
                  <Tooltip formatter={(value) => [value.toFixed(2), "Cohen's d"]} />
                  <Legend />
                  <Bar name="Effect Size (Cohen's d)" dataKey="effectSize">
                    {bandEffectSizeData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.significant ? '#4CAF50' : '#9E9E9E'} 
                      />
                    ))}
                    <LabelList dataKey="effectSize" position="top" formatter={(value) => value.toFixed(2)} />
                  </Bar>
                  <ReferenceLine y={0.2} stroke="#ccc" strokeDasharray="3 3" label={{ value: 'Small Effect', position: 'right', fill: '#666' }} />
                  <ReferenceLine y={0.5} stroke="#666" strokeDasharray="3 3" label={{ value: 'Medium Effect', position: 'right', fill: '#666' }} />
                  <ReferenceLine y={0.8} stroke="#333" strokeDasharray="3 3" label={{ value: 'Large Effect', position: 'right', fill: '#666' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              Band 2 showed the largest effect size (d=0.96) and was statistically significant, followed by Band 4 (d=0.54).
              The effect on Band 5 was minimal (d=0.24). Green bars indicate statistically significant effects (p&lt;0.05).
            </p>
          </div>
        </div>
        
        {/* Cluster Analysis Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', color: '#065F46' }}>
            Cluster Analysis
          </h2>
          <p style={{ marginBottom: '1rem', color: '#374151' }}>
            Cluster analysis helps identify natural groupings of students based on their performance patterns.
            We identified three distinct clusters with different characteristics and responses to the intervention.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ backgroundColor: '#F0FDF4', padding: '1rem', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#16A34A' }}>
                Cluster 1: Stable Performers
              </h3>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Size:</span> 3 students (1 exp, 2 control)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Initial Score:</span> 27.3/50 (Low-Medium)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Avg. Improvement:</span> -0.67 points
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Key Characteristic:</span> Slight decline in performance
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Media Profile:</span> Lower movie consumption (3.3/5)
              </p>
              <p style={{ fontSize: '0.875rem', fontStyle: 'italic', marginTop: '0.5rem' }}>
                This small cluster showed a slight decline in performance, particularly in Band 5.
              </p>
            </div>
            
            <div style={{ backgroundColor: '#F0FDF4', padding: '1rem', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#16A34A' }}>
                Cluster 2: Minimal Change Group
              </h3>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Size:</span> 44 students (19 exp, 25 control)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Initial Score:</span> 38.0/50 (High)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Avg. Improvement:</span> 0.09 points
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Key Characteristic:</span> Already high performers
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>CERF Level:</span> Mostly B2 (59%)
              </p>
              <p style={{ fontSize: '0.875rem', fontStyle: 'italic', marginTop: '0.5rem' }}>
                The largest cluster showed minimal change regardless of group. These were primarily high performers with little room for improvement.
              </p>
            </div>
            
            <div style={{ backgroundColor: '#F0FDF4', padding: '1rem', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#16A34A' }}>
                Cluster 3: High Improvers
              </h3>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Size:</span> 13 students (10 exp, 3 control)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Initial Score:</span> 28.6/50 (Medium-Low)
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Avg. Improvement:</span> 2.23 points
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Key Characteristic:</span> Strong improvement in Band 2
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                <span style={{ fontWeight: 600 }}>Media Profile:</span> Highest movie consumption (4.6/5)
              </p>
              <p style={{ fontSize: '0.875rem', fontStyle: 'italic', marginTop: '0.5rem' }}>
                This cluster showed the most improvement and was predominantly in the experimental group. They had significant gains in Band 2 (1.08 points).
              </p>
            </div>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              Cluster Composition by Group
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clusterCompositionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cluster" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar name="Experimental" dataKey="experimental" stackId="a" fill="#8884d8" />
                  <Bar name="Control" dataKey="control" stackId="a" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              This chart shows the composition of each cluster by group. Note that Cluster 3 (high improvers) is predominantly 
              experimental group students (10 experimental vs. 3 control). Cluster 2 has a more balanced composition but slightly 
              more control students.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              Average Improvement by Cluster
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clusterImprovementData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cluster" />
                  <YAxis domain={[-1, 3]} />
                  <Tooltip />
                  <Legend />
                  <Bar name="Average Improvement" dataKey="improvement" fill="#ff7300">
                    {clusterImprovementData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.improvement > 0 ? '#4CAF50' : '#F44336'} 
                      />
                    ))}
                    <LabelList dataKey="improvement" position="top" formatter={(value) => value.toFixed(2)} />
                  </Bar>
                  <ReferenceLine y={0} stroke="#000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              Cluster 3 showed dramatic improvement (+2.23 points) while Cluster 2 showed minimal change (+0.09 points) and 
              Cluster 1 showed a slight decline (-0.67 points). The size of each cluster is indicated in the tooltip.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              Band Improvement by Cluster
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={bandImprovementByCluster}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bandNum" label={{ value: 'Band Number', position: 'insideBottom', offset: -5 }} />
                  <YAxis domain={[-1, 1.5]} />
                  <Tooltip />
                  <Legend />
                  <Bar name="Cluster 1" dataKey="Cluster 1" fill="#8884d8" />
                  <Bar name="Cluster 2" dataKey="Cluster 2" fill="#82ca9d" />
                  <Bar name="Cluster 3" dataKey="Cluster 3" fill="#ff7300" />
                  <ReferenceLine y={0} stroke="#000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              This chart shows improvement by band for each cluster. Cluster 3 (high improvers) showed substantial gains in Band 2 (+1.08),
              while Cluster 1 showed a decline in Band 5 (-1.0). The pattern suggests the crash course primarily improved Band 2 skills
              for students in Cluster 3.
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
              CERF Level Distribution by Cluster
            </h3>
            <div style={{ height: '18rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cerfDistributionByCluster}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cluster" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {/* <Bar name="A2 (Basic)" dataKey="A2" stackId="a" fill="#F44336" /> */}
                  <Bar name="B1 (Intermediate)" dataKey="B1" stackId="a" fill="#FF9800" />
                  <Bar name="B2 (Upper Intermediate)" dataKey="B2" stackId="a" fill="#4CAF50" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#4B5563', marginTop: '0.5rem' }}>
              This chart shows the distribution of CERF levels within each cluster. Cluster 2 (minimal change) had the highest proportion
              of B2 (upper intermediate) students, while Cluster 3 (high improvers) had a more balanced mix of B1 and B2 students.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalysisDashboard;
